import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api, errText, photoUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Mail, MessageSquare, Send, Loader2, CheckCircle2, Megaphone, MapPin, ChevronDown, Search, Sparkles, ImagePlus, X } from "lucide-react";
import { useSiteEmails } from "../lib/siteContent";

const DESIGN_PLACEMENTS = [
  ["home_top", "Home top — premium leaderboard"],
  ["home", "Home leaderboard"],
  ["browse", "Browse strip"],
  ["browse_bottom", "Browse bottom strip"],
  ["browse_sidebar", "Browse sidebar box"],
  ["listing", "Listing rectangle"],
  ["not_sure", "Not sure yet — need advice"],
];
const placementLabel = (v) => (DESIGN_PLACEMENTS.find(([k]) => k === v) || [])[1] || "Not specified";

const FAQS = [
  { q: "How do I post a listing?", a: "Click \"Post Ad\" in the top bar, sign in or create a free account, then add your photos, description and price. Most listings go live within minutes." },
  { q: "Is it free to post?", a: "Yes — posting pets, supplies and services on CritterPosts.ca is free. Optional paid banner advertising is available for verified businesses." },
  { q: "How do I contact a seller?", a: "Open any listing and use the in-site message button, or the seller's shown contact details. Your email stays private until you choose to share it." },
  { q: "How does banner advertising work?", a: "Verified business accounts can book banner ads from our Advertising page. Pricing starts at CAD $5/day with discounts for longer runs, paid by Interac e-Transfer." },
  { q: "How do I report a suspicious listing or user?", a: "Every listing and profile has a \"Report\" option. Our moderation team reviews reports quickly to keep the marketplace safe." },
];

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0" data-testid="contact-faq-item">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 py-4 text-left" data-testid="contact-faq-toggle">
        <span className="font-semibold text-[#12234A] text-sm">{q}</span>
        <ChevronDown size={18} className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-all ${open ? "max-h-40 pb-4" : "max-h-0"}`}>
        <p className="text-sm text-slate-500">{a}</p>
      </div>
    </div>
  );
};

export default function ContactUs() {
  const emails = useSiteEmails();
  const { user } = useAuth();
  const location = useLocation();
  const presetSubject = new URLSearchParams(location.search).get("subject") || "";
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", subject: presetSubject, message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [faqQuery, setFaqQuery] = useState("");
  const designMode = /design|conception/i.test(presetSubject);
  const [designPlacement, setDesignPlacement] = useState("");
  const [brandColours, setBrandColours] = useState("");
  const [logos, setLogos] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const uploadLogos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (logos.length + files.length > 3) return toast.error("Up to 3 reference images");
    setUploadingLogo(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: res } = await api.post("/ads/preview-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setLogos((l) => [...l, res.path]);
      }
    } catch (err) { toast.error(errText(err)); }
    finally { setUploadingLogo(false); }
  };
  const removeLogo = (p) => setLogos((l) => l.filter((x) => x !== p));

  // When arriving via a prefilled link (e.g. "Message our design team"), scroll to the form.
  useEffect(() => {
    if (presetSubject) {
      setTimeout(() => document.getElementById("contact-form-section")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const extras = designMode
      ? `[Banner design request]\nPlacement: ${placementLabel(designPlacement)}\nBrand colours: ${brandColours.trim() || "Not specified"}\n\n`
      : "";
    const finalMessage = (extras + form.message.trim()).trim();
    if (!form.name.trim() || !form.email.trim() || finalMessage.length < 10) {
      return toast.error("Please add your name, email and a message (10+ characters)");
    }
    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(), email: form.email.trim(),
        subject: form.subject.trim(), message: finalMessage,
        attachments: designMode ? logos : [],
      });
      setSent(true);
    } catch (err) { toast.error(errText(err)); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#F5551E] bg-[#FFF4F0] rounded-full px-3 py-1"><MessageSquare size={13} /> We're here to help</span>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#12234A] mt-4">Contact us</h1>
        <p className="text-base text-slate-500 mt-3">Questions about a listing, your account, or advertising with CritterPosts.ca? Send us a note and our team will get back to you.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-10" id="contact-form-section" data-testid="contact-form-section">
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="contact-info-email">
            <div className="w-11 h-11 rounded-xl bg-[#FFF4F0] flex items-center justify-center"><Mail size={20} className="text-[#F5551E]" /></div>
            <h3 className="font-display font-bold text-[#12234A] mt-3">Email us</h3>
            <p className="text-sm text-slate-500 mt-1">General enquiries</p>
            <a href={`mailto:${emails.contact}`} className="text-sm font-semibold text-[#F5551E] hover:underline" data-testid="contact-page-email">{emails.contact}</a>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="contact-info-advertising">
            <div className="w-11 h-11 rounded-xl bg-[#FFF4F0] flex items-center justify-center"><Megaphone size={20} className="text-[#F5551E]" /></div>
            <h3 className="font-display font-bold text-[#12234A] mt-3">Advertising</h3>
            <p className="text-sm text-slate-500 mt-1">Payments &amp; banner ads</p>
            <a href={`mailto:${emails.payment}`} className="text-sm font-semibold text-[#F5551E] hover:underline" data-testid="contact-page-payment-email">{emails.payment}</a>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="contact-info-location">
            <div className="w-11 h-11 rounded-xl bg-[#FFF4F0] flex items-center justify-center"><MapPin size={20} className="text-[#F5551E]" /></div>
            <h3 className="font-display font-bold text-[#12234A] mt-3">Based in Canada</h3>
            <p className="text-sm text-slate-500 mt-1">Serving pet lovers coast to coast 🇨🇦</p>
          </div>
        </div>

        <div className="lg:col-span-2">
          {sent ? (
            <div className="bg-white rounded-2xl border border-emerald-200 p-10 text-center h-full flex flex-col items-center justify-center" data-testid="contact-success">
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center"><CheckCircle2 size={28} className="text-emerald-600" /></div>
              <h3 className="font-display font-bold text-2xl text-[#12234A] mt-4">Message sent!</h3>
              <p className="text-sm text-slate-500 mt-2 max-w-sm">Thanks for reaching out — we've received your message and will reply to <strong>{form.email}</strong> soon.</p>
              <Button onClick={() => { setSent(false); setForm((f) => ({ ...f, subject: "", message: "" })); }} variant="outline" data-testid="contact-send-another" className="rounded-xl mt-6">Send another message</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200/80 p-6 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-500">Your name</Label>
                  <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Jane Doe" data-testid="contact-name" className="mt-1.5 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="you@example.com" data-testid="contact-email" className="mt-1.5 rounded-xl" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500">Subject (optional)</Label>
                <Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} placeholder="What's this about?" data-testid="contact-subject" className="mt-1.5 rounded-xl" maxLength={150} />
              </div>
              {designMode && (
                <div className="rounded-xl border border-[#F5551E]/20 bg-[#FFF9F6] p-4 space-y-3" data-testid="design-request-fields">
                  <p className="text-xs font-bold text-[#F5551E] uppercase tracking-wide flex items-center gap-1.5"><Sparkles size={13} /> Banner design request</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-slate-500">Which placement?</Label>
                      <Select value={designPlacement} onValueChange={setDesignPlacement}>
                        <SelectTrigger data-testid="design-placement" className="mt-1.5 rounded-xl bg-white"><SelectValue placeholder="Choose a placement" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {DESIGN_PLACEMENTS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`design-placement-${v}`}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-500">Brand colours</Label>
                      <Input value={brandColours} onChange={(e) => setBrandColours(e.target.value)} placeholder="e.g. navy & orange, or #12234A" data-testid="design-colours" className="mt-1.5 rounded-xl" maxLength={120} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">Logo / reference images (optional)</Label>
                    <div className="flex flex-wrap gap-2 mt-1.5" data-testid="design-logos">
                      {logos.map((p) => (
                        <div key={p} className="relative group">
                          <img src={photoUrl(p)} alt="reference" className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
                          <button type="button" onClick={() => removeLogo(p)} data-testid={`design-logo-remove-${logos.indexOf(p)}`} className="absolute -top-1.5 -right-1.5 bg-[#12234A] text-white rounded-full p-0.5 shadow"><X size={12} /></button>
                        </div>
                      ))}
                      {logos.length < 3 && (
                        <label data-testid="design-logo-upload" className="h-16 w-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-[#F5551E] hover:bg-[#FFF9F6] transition-colors">
                          {uploadingLogo ? <Loader2 size={18} className="animate-spin text-[#F5551E]" /> : <ImagePlus size={18} className="text-slate-400" />}
                          <input type="file" accept="image/*" multiple className="hidden" onChange={uploadLogos} disabled={uploadingLogo} />
                        </label>
                      )}
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400">Tell us your placement and brand colours below — add any logo, text or offer details in the message and we'll design it for you.</p>
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold text-slate-500">Message</Label>
                <Textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} placeholder="How can we help?" data-testid="contact-message" className="mt-1.5 rounded-xl min-h-[160px]" maxLength={4000} />
              </div>
              <Button type="submit" disabled={sending} data-testid="contact-submit" className="w-full rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2 h-11 disabled:opacity-40">
                {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />} Send message
              </Button>
              <p className="text-[11px] text-slate-400 text-center">We never share your email. We'll only use it to reply to your message.</p>
            </form>
          )}
        </div>
      </div>

      <div className="mt-14 max-w-3xl mx-auto" data-testid="contact-faq">
        <h2 className="font-display font-bold text-2xl text-[#12234A] text-center">Frequently asked questions</h2>
        <p className="text-sm text-slate-500 text-center mt-2">A quick answer might save you a message.</p>
        <div className="relative mt-5 max-w-md mx-auto">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={faqQuery} onChange={(e) => setFaqQuery(e.target.value)} placeholder="Search FAQs…" data-testid="faq-search" className="rounded-full pl-10 h-11" />
        </div>
        {(() => {
          const q = faqQuery.trim().toLowerCase();
          const filtered = q ? FAQS.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)) : FAQS;
          return filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-8 mt-6 text-center" data-testid="faq-no-results">
              <p className="text-sm text-slate-500">No FAQs match "<strong>{faqQuery}</strong>". Try a different word, or send us a message below.</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-5 sm:p-6 mt-6" data-testid="faq-list">
              {filtered.map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
            </div>
          );
        })()}
        <div className="text-center mt-6" data-testid="faq-still-need-help">
          <p className="text-sm text-slate-500">Still have a question?</p>
          <a
            href="#contact-form-section"
            data-testid="faq-jump-to-form"
            onClick={(e) => { e.preventDefault(); const el = document.getElementById("contact-form-section"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}
            className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-[#F5551E] hover:gap-2.5 transition-all"
          >
            <MessageSquare size={15} /> Still need help? Send us a message
          </a>
        </div>
      </div>
    </div>
  );
}
